/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { FunctionComponent } from 'react';
import type { GroupReportMetricsInterface, TimeAssessmentFilterEnum } from '@whizz/api-model';
import type { HandleDownloadModalProps } from '../../global/modals/HandleDownloadModal';
import type { ManageMetricsModalProps } from './modules/modals/ManageMetricsModal';
import type { ManageMetricsType } from '../../forms/types';
import type { ClassReportStudentType, ClassReportType, PdfFilterType, ReportsFilterOptionsType } from '../types';
import type { Teacher } from '@whizz/api-model/dist/@types';
import type { TableReportType, TopicChartType } from '../../mapper/groupTopicReportMapper';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import HandleDownloadModal from '../../global/modals/HandleDownloadModal';
import { GlobalDashboardLayout } from '../../layouts';
import { Settings } from './modules';
import { useClassSelector } from '../../hooks/filters/useClassSelector';
import { useFilterSelector } from '../../hooks/filters/useFilterSelector';
import { useDateRangeSelector } from '../../hooks/filters/useDateRangeSelector';
import { useReportViewToggle } from '../../hooks/filters/useReportViewToggle';
import { Alert, Button, ButtonIcon } from '../../components';
import { useModal } from '../../hooks/useModal';
import { GLOBAL_ICONS, LAST_WEEKS, QUERIES_IDS } from '../../util/vars';
import { mapAllGroupReportForClassReport } from '../../mapper/groupMetricsMapper';
import { getDatesParams } from '../../helper/getDatesParams';
import getClassReportStudentsFiltered from '../../helper/getClassReportStudentsFiltered';
import { useQuery } from 'react-query';
import { StudentStatusEnum, TickStatusEnum } from '@whizz/api-model';
import { model, repository } from '@whizz/api-client';
import { Headline } from '../../global';
import PdfFilterButton from '../../global/buttons/PdfFilterButton';
import ReportBody from './sections/ReportBody';
import { dateTimeToDateString } from '../../util/dates';
import { DateTime } from 'luxon';
import { PdfContext } from '../../context/PdfContext';
import { default_metrics_settings } from '../types';
import ManageMetricsModal from './modules/modals/ManageMetricsModal';
import styled from 'styled-components';
import { useLocalStorage } from 'react-use';
import NoClasses from '../../global/sections/NoClasses';
import NoData from '../../global/sections/NoData';

type Props = {
  pdfIds?: number[];
  pdfFilter?: PdfFilterType;
};

const Reports: FunctionComponent<Props> = ({
  pdfIds,
  pdfFilter,
}: Props) => {
  const { locale, translate } = useI18n();
  const { me } = useIdentity();
  const teacherId = (me as Teacher).getId();
  const [ hideForPdf, SetHideForPdf ] = useState<boolean>(false);
  const filteredIdsRef = useRef<number[]>([]);

  const classesQuery = useQuery(
    [ QUERIES_IDS.CLASSES, teacherId ],
    () => repository.NewTeacherRepository.getClasses(teacherId!),
    {
      enabled: null != teacherId,
      placeholderData: [],
    },
  );

  const { isPdf: isBuildingPdf } = useContext(PdfContext);

  const schoolId = null != classesQuery.data && 0 < classesQuery.data.length && null != classesQuery.data[0].schoolId
    ? classesQuery.data[0].schoolId
    : null;

  // Class Selection
  const [ classSelector, classValue ] = useClassSelector(me?.username as string, classesQuery.data!, 'space-between');
  const selectedClassId = isBuildingPdf ? pdfFilter?.classSelectedId : classValue?.value;

  // Date Interval Selection
  const academicYearStartDateQuery = useQuery<DateTime>(
    [ QUERIES_IDS.ACADEMIC_YEAR_START_DATE, schoolId ],
    () => model.School.academicYearStartDate(schoolId!),
    {
      enabled: null != schoolId,
    },
  );
  const [ timeRangeSelector, timeRangeValue, timeRangeCustomValue ] = useDateRangeSelector({
    defaultValue: 'last_week',
    showLabel: pdfFilter ? translate(`settings.DATE_RANGE.OPTIONS.${pdfFilter.dateIntervalSelected}`, { weeks: LAST_WEEKS }) : undefined,
    maxDate: new Date(),
  });
  const selectedDateInterval = (isBuildingPdf ? pdfFilter?.dateIntervalSelected : timeRangeValue?.value) ?? 'last_week';
  const selectedTimeRange = {
    value: selectedDateInterval,
    label: translate(`settings.DATE_RANGE.OPTIONS.${selectedDateInterval}`, { weeks: LAST_WEEKS }),
  };
  const {
    startDateParam,
    endDateParam,
  } = getDatesParams(selectedDateInterval, timeRangeCustomValue, academicYearStartDateQuery.data ?? null, me);
  const selectedStartDate = isBuildingPdf ? pdfFilter?.startDate : startDateParam;
  const selectedEndDate = endDateParam;

  const isValidDate = selectedDateInterval !== 'custom' || (selectedDateInterval === 'custom' && DateTime.isDateTime(selectedStartDate) && DateTime.isDateTime(selectedEndDate));
  const queryIsEnabled = null != selectedClassId && isValidDate;

  // Chart or Table Selection
  const [ viewToggle, viewValue ] = useReportViewToggle();

  const downloadModalInitialProps = {
    title: translate('pages.REPORTS.DOWNLOAD.MODAL.title'),
    students: [],
    classId: selectedClassId,
    className: classValue?.label,
    dateInterval: selectedDateInterval,
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    isTableView: viewValue === 'table',
  };

  const [
    handleDownloadModal,
    toggleHandleDownloadModal,
    setPropsForHandleDownloadModal,
    downloadModalIsOpen,
  ] = useModal(
    (props: HandleDownloadModalProps) => (
      <HandleDownloadModal {...props} />
    ),
    downloadModalInitialProps,
  );

  const selectedStartDateString = dateTimeToDateString(selectedStartDate!);
  const selectedEndDateString = dateTimeToDateString(selectedEndDate!);

  // The query used to map the query data needs to be memoized, or it will be regenerated on each render
  const mapQueryData = useCallback(
    (data: [ GroupReportMetricsInterface, GroupReportMetricsInterface, GroupReportMetricsInterface ]) => mapAllGroupReportForClassReport(...data),
    [],
  );
  const groupTags = useQuery([ selectedClassId ], () => repository.TagsRepository.getTags());

  const groupReportMetricsQuery = useQuery<[
    GroupReportMetricsInterface,
    GroupReportMetricsInterface,
    GroupReportMetricsInterface,
  ], unknown, {
    classReport: ClassReportType,
    chartTopic: TopicChartType[],
    tableTopic: TableReportType
  }>(
    [
      QUERIES_IDS.GROUP_REPORT_METRICS,
      selectedClassId,
      selectedStartDateString,
      selectedEndDateString,
    ],
    () => Promise.all([
      repository.GroupReportMetricsRepository.oneWithAllStudentsOfGroup(
        selectedClassId,
        locale,
        selectedStartDate as DateTime | TimeAssessmentFilterEnum,
        selectedEndDate || undefined,
      ),
      repository.GroupReportMetricsRepository.oneWithAssessedNotBeingReassessedStudents(
        selectedClassId,
        locale,
        selectedStartDate as DateTime | TimeAssessmentFilterEnum,
        selectedEndDate || undefined,
      ),
      repository.GroupReportMetricsRepository.oneWithUnassessedOrReassessedStudent(
        selectedClassId,
        locale,
        selectedStartDate as DateTime | TimeAssessmentFilterEnum,
        selectedEndDate || undefined,
      ),
    ]),
    {
      enabled: queryIsEnabled,
      keepPreviousData: true,
      placeholderData: () => {
        const placeholderData = placeHolderGroupReportMetrics(selectedClassId);

        return [ placeholderData, placeholderData, placeholderData ];
      },
      select: mapQueryData,
    },
  );

  const isLoading = queryIsEnabled && null != classValue && (!groupReportMetricsQuery.isFetched || groupReportMetricsQuery.isFetching);
  const groupTagsThatStudentsHave = useMemo(() => groupTags.data?.filter((tag) => {
    const studentHasFilter = groupReportMetricsQuery.data?.classReport.students.all.filter((student) => {
      return student.tags.includes(tag.id);
    });
    return !!studentHasFilter?.length;
  }), [ groupReportMetricsQuery.data?.classReport.students.all, groupTags.data ]);

  const [
    filterButton,
    filterValue,
    showOthersData,
    filterLabel,
    clearFilters,
    setNumberOfFilteredStudents,
    setFilter,
  ] = useFilterSelector(groupReportMetricsQuery.data?.classReport.students.all ?? [], groupTagsThatStudentsHave?.sort((a, b) => {
    return (a.abbreviation < b.abbreviation ? -1 : (a.abbreviation > b.abbreviation) ? 1 : 0);
  }) ?? null);

  const filteredStudentsIds = useMemo(
    () => getClassReportStudentsFiltered(groupReportMetricsQuery.data?.classReport.students.all ?? [], filterValue).map((st) => st.id),
    [ groupReportMetricsQuery.data?.classReport, filterValue ],
  );

  const [ settings, setSettings ] = useLocalStorage<ManageMetricsType>(`report_settings_${me?.username}`, {
    default_settings: default_metrics_settings,
    additional_settings: [],
  });

  const handleSubmit = (values: ManageMetricsType)  => {
    setSettings(values);
  };

  const handleStudentClick = (studentId: number) => {
    const filteredIds = filteredIdsRef.current;
    const isStudentHighlighted = filteredIds.includes(studentId);
    setFilter({ student: isStudentHighlighted ? [] : [ studentId ] }, false);
  };

  const [ manageMetricsModal, toggleManageMetricsModal, setPropsForManageMetricsModal ] = useModal((props: ManageMetricsModalProps) => (
    <ManageMetricsModal {...props} />
  ), {
    title: translate('components.BUTTONS.MANAGE_METRICS'),
    icon: GLOBAL_ICONS.settings,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    clearFilters(false);
  }, [ selectedClassId ]);

  useEffect(() => {
    if (filterValue) {
      const filter = Object.keys(filterValue)[0] as ReportsFilterOptionsType;
      if (filter === 'student') {
        setNumberOfFilteredStudents(null);
      } else {
        setNumberOfFilteredStudents(filteredStudentsIds.length);
      }
    } else {
      setNumberOfFilteredStudents(null);
    }
  }, [ filterValue, filteredStudentsIds ]);

  const content = (pdfStudent?: ClassReportStudentType) => {
    if (null == classValue && !isBuildingPdf) {
      const showNoClasses = classesQuery.isFetched && 0 === classesQuery.data?.length;
      return (
        showNoClasses
          ? <NoClasses />
          : <Alert show>{translate('pages.HOME.SELECT_CLASS')}</Alert>
      );
    }

    if (isLoading) {
      // The loader will overwrite it
      return null;
    }

    if (groupReportMetricsQuery.isSuccess && 0 === groupReportMetricsQuery.data.classReport.students.all.length) {
      return (
        <>
          <Headline title={translate('pages.HOME.GET_STARTED.NO_STUDENTS')} />
          <div className={'text-muted italic mt-5 space-y-2'}>
            <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info1')}</p>
            <p>{translate('pages.HOME.GET_STARTED.NO_STUDENTS_info2')}</p>
          </div>
        </>
      );
    }

    return (
      <>
        {groupReportMetricsQuery.data?.classReport.mathsAgeAverage === 0 ? <NoData alert /> : null}
        <ReportBody
          viewValue={pdfStudent ? 'charts' : viewValue}
          labelValue={pdfStudent ? pdfStudent.fullName : filterLabel}
          timeRangeValue={timeRangeValue ? { ...timeRangeValue, endDate: endDateParam } : null}
          filteredStudentsIds={pdfStudent ? [ pdfStudent.id ] : filteredStudentsIds}
          showOthersData={pdfStudent ? false : showOthersData}
          classReport={groupReportMetricsQuery.data!.classReport}
          chartTopicProgress={groupReportMetricsQuery.data!.chartTopic}
          pdfFilter={pdfFilter}
          metricsSettings={settings}
          dateRange={{
            startDate: selectedStartDate,
            endDate: selectedEndDate,
          }}
          groupId={selectedClassId}
          onElementClick={handleStudentClick}
        />
      </>
    );
  };

  const classSelectorButton = <PdfFilterButton showLabel={classesQuery.data!.find((cl) => cl.id == pdfFilter?.classSelectedId)?.name || ''} />;
  const filterSelectorButton = <PdfFilterButton showLabel={selectedTimeRange.label} />;

  useEffect(() => {
    if (downloadModalIsOpen) return;

    SetHideForPdf(false);
  }, [ downloadModalIsOpen ]);

  useEffect(() => {
    filteredIdsRef.current = filteredStudentsIds;
  }, [ filteredStudentsIds ]);

  return (
    <div id="reports-main-container">
      {(isBuildingPdf && (pdfIds && pdfIds.length > 0))
        ? (
          pdfIds.map((id, index) => {
            const student = groupReportMetricsQuery.data!.classReport.students.all.find((st) => id === st.id);
            const pdfFilterButton = <PdfFilterButton showLabel={student ? student.fullName : ''} />;
            return (
              <React.Fragment key={id}>
                <GlobalDashboardLayout
                  isLoading={isLoading}
                  disableContainer
                  title={translate('pages.REPORTS.HEADER')}
                  actionContent={classSelectorButton}
                  settings={(
                    <Settings
                      filterSelector={pdfFilterButton}
                      timeRangeSelector={filterSelectorButton}
                      hideDownloadButton={isBuildingPdf}
                    />)}
                  isBuildingPdf={isBuildingPdf}
                  isPdfLastPage={index === pdfIds.length - 1}
                  hideHeader={isBuildingPdf}
                >
                  {content(student)}
                </GlobalDashboardLayout>
                <br style={{ pageBreakAfter: 'always' }} className='my-lg'/>
              </React.Fragment>
            );
          })
        )
        : (
          <GlobalDashboardLayout
            isLoading={isLoading}
            disableContainer
            title={translate('pages.REPORTS.HEADER')}
            actionContent={classSelector}
            settings={(
              <Settings
                filterSelector={filterButton}
                timeRangeSelector={timeRangeSelector}
                viewTypeToggle={viewToggle}
                manageMetricsHandler={(
                  <ManageMetricsButton
                    color={'white'}
                    icon={GLOBAL_ICONS.settings}
                    size={'sm'}
                    disabled={viewValue !== 'table'}
                    onClick={() => {
                      setPropsForManageMetricsModal((prev) => ({
                        ...prev,
                        settings: settings,
                        onSubmit: handleSubmit,
                      }));
                      toggleManageMetricsModal(true);
                    }}
                    data-cy="button-manage-metrics"
                  >
                    {translate('components.BUTTONS.MANAGE_METRICS')}
                  </ManageMetricsButton>
                )}
                downloadHandler={(
                  <ButtonIcon
                    icon={GLOBAL_ICONS.download}
                    color={'white'}
                    isCircle
                    onClick={() => {
                      setPropsForHandleDownloadModal({
                        ...downloadModalInitialProps,
                        students: groupReportMetricsQuery.data!.classReport.students.all,
                        isTable: viewValue === 'table',
                      });
                      toggleHandleDownloadModal(true);
                      SetHideForPdf(true);
                    }}
                    disabled={isLoading || !groupReportMetricsQuery.data!.classReport || groupReportMetricsQuery.data!.classReport.students.all.length === 0}
                  />
                )}
                hideDownloadButton={isBuildingPdf || hideForPdf}
              />)}
            hideHeader={isBuildingPdf || hideForPdf}
            fullWidthContent={viewValue === 'table'}
            sticky={viewValue === 'charts'}
            classId={classValue?.value}
          >
            {manageMetricsModal}
            {handleDownloadModal}
            {content()}
          </GlobalDashboardLayout>)}
    </div>
  );
};

export default Reports;

const ManageMetricsButton = styled(Button)`
  border-radius: ${(props) => props.theme.radius.input};
`;

const placeHolderGroupReportMetrics = (groupId: number): GroupReportMetricsInterface => ({
  id: groupId,
  startDate: DateTime.now(),
  endDate: DateTime.now(),
  studentsMetrics: [],
  filter: null,
  average: {
    mathsAge: 0,
    mathsAgeImprovement: 0,
    progressions: 0,
    learningTime: 0,
    consistency: 0,
    timeSpan: 0,
    mathsAgeAtStart: 0,
    tutorUsage: 0,
    topicChallengeUsage: 0,
    topicChallengesPassed: 0,
    totalTopicChallenges: 0,
    progressionPassRate: 0,
    exerciseMark: 0,
    testMark: 0,
    actualAge: 0,
    minutesPerProgression: 0,
    progressionsPerWeek: 0,
    tickStatus: TickStatusEnum.GREEN,
    studentStatus: StudentStatusEnum.SUPERSTAR,
  },
  studentsTopicProgress: [],
  averageTopicProgress: [],
});
